import { AuthContext, CommonContext } from '@repo-level/core-front';
import { FC, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { OptionsBlock } from 'components/form/primitives/SelectBlock';
import { ModalsContext, ModalType } from 'contexts/modal';
import { Button } from 'components/buttons';
import {
  useCurrentOptionWallet,
  useFormatCurrentOptionWallet,
} from 'hooks/wallet';
import { useTheme } from 'hooks/theme';
import { useComponentVisible } from 'hooks/visibility';
import { amountWithCurrencySymbol } from 'helpers/currency';
import { getCurrencyCentsAndCode } from 'helpers/wallet';

import { ScUserWallets, ScUserWalletsProps } from './styled';
import { useMedia } from 'modules/common/hooks/useMedia';

interface UserWalletsProps {
  hideDepositButton?: boolean;
  disableSelectionForOneCurrency?: boolean;
  onClickDepositButton?: () => void;
  onClickCurrencySelectButton?: () => void;
}

export const UserWallets: FC<
  UserWalletsProps & Pick<ScUserWalletsProps, 'menuMode' | 'darkSelector'>
> = ({
  hideDepositButton,
  disableSelectionForOneCurrency,
  menuMode,
  onClickDepositButton,
  onClickCurrencySelectButton,
  ...props
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('glossary');
  const { isMobile } = useMedia();

  const {
    ref,
    openerRef,
    isComponentVisible,
    setIsComponentVisible,
  } = useComponentVisible(false);

  const { currencies } = useContext(CommonContext);
  const { wallets, changeUserWallet, pendingWallets, pendingUser } = useContext(
    AuthContext
  );
  const { handleModal } = useContext(ModalsContext);

  const {
    preloaderDotsAnimation: PreloaderDotsAnimation,
    arrowRightSmallIcon: RightSmallIcon,
  } = useTheme();

  const currentOptionWallet = useCurrentOptionWallet();
  const formatedCurrentOptionValue = useFormatCurrentOptionWallet(true);

  const optionsWallets = useMemo(() => {
    return wallets
      .map((wallet) => {
        const { currency, amount } = getCurrencyCentsAndCode(
          wallet.amountCents,
          wallet.currency,
          currencies
        );

        return {
          name: amountWithCurrencySymbol(currency, amount),
          value: wallet.currency,
        };
      })
      .filter((option) => option.value !== currentOptionWallet.currency);
  }, [wallets, currencies, currentOptionWallet]);

  const loading = useMemo(() => pendingWallets || pendingUser, [
    pendingWallets,
    pendingUser,
  ]);

  const depositButtonProps = useMemo(
    () => (menuMode ? { xxLarge: true } : { large: true, wide: true }),
    [menuMode]
  );

  const isDisabledSelection = useMemo(
    () => !optionsWallets.length && disableSelectionForOneCurrency,
    [disableSelectionForOneCurrency, optionsWallets.length]
  );

  const handleOpenSelector = useCallback(() => {
    if (isDisabledSelection) return;

    onClickCurrencySelectButton && onClickCurrencySelectButton();

    isMobile
      ? handleModal(ModalType.MyCurrencies, {
          stayInSameWindow: true,
        })()
      : setIsComponentVisible(!isComponentVisible);
  }, [
    handleModal,
    isDisabledSelection,
    isComponentVisible,
    isMobile,
    setIsComponentVisible,
    onClickCurrencySelectButton,
  ]);

  return (
    <ScUserWallets
      isActive={isComponentVisible}
      menuMode={menuMode}
      isDisabledSelection={isDisabledSelection}
      {...props}
    >
      <div className="select-wallet">
        {menuMode && <div className="balance">Balance</div>}
        {loading ? (
          <div className="loader">
            Loading
            <PreloaderDotsAnimation />
          </div>
        ) : (
          <button
            ref={openerRef}
            className={`current-wallet ${language}`}
            type="button"
            onClick={handleOpenSelector}
          >
            <div className="wallet-value">
              <p>{formatedCurrentOptionValue}</p>
              {!isDisabledSelection && (
                <div className="arrow">
                  <RightSmallIcon />
                </div>
              )}
            </div>
          </button>
        )}
        {isComponentVisible && !isMobile && (
          <div className="options">
            <OptionsBlock
              ref={ref}
              paddingTop={36}
              hideEmptyText
              options={optionsWallets}
              onChange={(value) => {
                setIsComponentVisible(!isComponentVisible);
                changeUserWallet(value as string);
              }}
              addons={
                !hideDepositButton ? (
                  <Button
                    type="button"
                    whiteTransparent
                    wide
                    small
                    onClick={handleModal(ModalType.EditCurrencies)}
                  >
                    {t('edit')}
                  </Button>
                ) : null
              }
            />
          </div>
        )}
      </div>
      {!hideDepositButton && (
        <Button
          className="dep-btn"
          onClick={() => {
            onClickDepositButton && onClickDepositButton();
            handleModal(ModalType.Deposit)();
          }}
          green
          {...depositButtonProps}
        >
          {t('deposit')}
        </Button>
      )}
    </ScUserWallets>
  );
};
